import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * stripMarkdown — Quick Markdown-to-plain-text for preview/truncated contexts.
 * Does NOT produce perfectly formatted output — just strips syntax markers
 * for single-line previews (e.g. line-clamp-2 in trainer list views).
 */
export function stripMarkdown(md) {
  if (!md) return ''
  return md
    .replace(/^#{1,6}\s+/gm, '')        // headings
    .replace(/\*\*(.+?)\*\*/g, '$1')     // bold
    .replace(/(?<!\w)\*([^*]+?)\*(?!\w)/g, '$1') // italic
    .replace(/__(.+?)__/g, '$1')         // bold underscore
    .replace(/`([^`]+?)`/g, '$1')        // inline code
    .replace(/^>\s?/gm, '')              // blockquotes
    .replace(/^[-*]\s+/gm, '• ')         // bullet lists
    .replace(/^\d+\.\s+/gm, '')          // numbered lists
    .replace(/\[([^\]]+?)\]\([^)]+?\)/g, '$1') // links
    .replace(/!\[([^\]]+?)\]\([^)]+?\)/g, '$1') // images
    .replace(/^---+$/gm, '')             // horizontal rules
    .replace(/\n{2,}/g, '\n')            // collapse multiple newlines
    .trim()
}

// Headings that represent special lesson sections — given subtle visual distinction
const SPECIAL_SECTIONS = [
  'practical exercise',
  'practical workshop',
  'common beginner mistakes',
  'common mistakes',
  'key takeaways',
  'practical checkpoint',
  'final checkpoint',
  'example',
  'challenge brief',
  'trainer guidance',
  'submission format',
]

function isSpecialSection(text) {
  if (!text) return false
  const lower = text.toLowerCase().trim()
  return SPECIAL_SECTIONS.some(s => lower === s || lower.startsWith(s))
}

/**
 * MarkdownContent — Safe educational Markdown renderer for the SMA LMS.
 *
 * Uses react-markdown (virtual DOM, no dangerouslySetInnerHTML) + remark-gfm
 * (tables, strikethrough, task lists). Renders lesson and assignment content
 * with clean educational typography using the existing SMA cinema design system.
 *
 * Props:
 *   content    — Markdown string to render
 *   compact    — If true, uses smaller text sizes (for inline previews / cards)
 *   className  — Optional wrapper className
 */
export default function MarkdownContent({ content, compact = false, className = '' }) {
  if (!content || !content.trim()) return null

  const textSize = compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-[15px]'
  const headingSize = compact ? 'text-sm sm:text-base' : 'text-lg sm:text-xl'
  const subHeadingSize = compact ? 'text-xs sm:text-sm' : 'text-base sm:text-lg'
  const minorHeadingSize = compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'

  return (
    <div className={`markdown-content ${textSize} text-cinema-soft leading-relaxed ${className}`}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // H1 — Lesson title level (large, gold, display font)
          h1: ({ children }) => {
            const text = typeof children === 'string' ? children : (children?.[0] || '')
            const special = isSpecialSection(String(text))
            return (
              <h1 className={`font-display ${headingSize} text-cinema-gold font-medium mt-6 mb-4 first:mt-0 tracking-tight`}>
                {children}
              </h1>
            )
          },
          // H2 — Major section heading (white, display font, with bottom border)
          // Special sections (Practical Exercise, Key Takeaways, etc.) get subtle background
          h2: ({ children }) => {
            const text = typeof children === 'string' ? children : (children?.[0] || '')
            const special = isSpecialSection(String(text))
            if (special) {
              return (
                <h2 className={`font-display ${subHeadingSize} text-cinema-glow font-semibold mt-6 mb-3 first:mt-0 px-3 py-2 bg-cinema-glow/8 border-l-2 border-cinema-glow/50 rounded-r`}>
                  {children}
                </h2>
              )
            }
            return (
              <h2 className={`font-display ${subHeadingSize} text-white font-medium mt-6 mb-3 pb-2 border-b border-white/10 first:mt-0`}>
                {children}
              </h2>
            )
          },
          // H3 — Subsection heading (glow accent, semibold)
          h3: ({ children }) => (
            <h3 className={`font-sans ${minorHeadingSize} text-cinema-glow font-semibold mt-5 mb-2 first:mt-0`}>
              {children}
            </h3>
          ),
          // H4 — Minor heading (white, semibold, smaller)
          h4: ({ children }) => (
            <h4 className={`font-sans ${minorHeadingSize} text-white font-semibold mt-4 mb-2 first:mt-0`}>
              {children}
            </h4>
          ),
          // H5 — Minor heading (soft, medium)
          h5: ({ children }) => (
            <h5 className={`font-sans ${compact ? 'text-xs' : 'text-sm'} text-cinema-soft font-medium mt-3 mb-1.5 first:mt-0`}>
              {children}
            </h5>
          ),
          // H6 — Minor heading (muted, medium)
          h6: ({ children }) => (
            <h6 className={`font-sans ${compact ? 'text-xs' : 'text-sm'} text-cinema-muted font-medium mt-3 mb-1 first:mt-0`}>
              {children}
            </h6>
          ),
          // Paragraphs — comfortable spacing, readable line height
          p: ({ children }) => (
            <p className={`font-sans ${textSize} text-cinema-soft leading-[1.7] my-3`}>
              {children}
            </p>
          ),
          // Unordered lists — clean indentation with custom markers
          ul: ({ children }) => (
            <ul className={`font-sans ${textSize} text-cinema-soft leading-relaxed my-3 ml-5 space-y-1.5 list-disc list-outside marker:text-cinema-glow/70`}>
              {children}
            </ul>
          ),
          // Ordered lists — numbered with glow markers
          ol: ({ children }) => (
            <ol className={`font-sans ${textSize} text-cinema-soft leading-relaxed my-3 ml-5 space-y-1.5 list-decimal list-outside marker:text-cinema-glow marker:font-semibold`}>
              {children}
            </ol>
          ),
          // List items
          li: ({ children }) => (
            <li className="pl-1.5">{children}</li>
          ),
          // Bold — white emphasis
          strong: ({ children }) => (
            <strong className="font-semibold text-white">
              {children}
            </strong>
          ),
          // Italic — soft emphasis
          em: ({ children }) => (
            <em className="italic text-cinema-soft">
              {children}
            </em>
          ),
          // Inline code — monospace with subtle background
          code: ({ children }) => (
            <code className="font-mono text-xs sm:text-sm text-cinema-glow bg-white/5 px-1.5 py-0.5 rounded">
              {children}
            </code>
          ),
          // Code blocks — monospace with graphite background
          pre: ({ children }) => (
            <pre className="font-mono text-xs sm:text-sm text-cinema-soft bg-cinema-graphite border border-white/8 rounded p-3 my-4 overflow-x-auto">
              {children}
            </pre>
          ),
          // Blockquotes — visually distinct with left border and subtle background
          // Used for key principles, examples, and callouts
          blockquote: ({ children }) => (
            <blockquote className={`font-sans ${textSize} text-cinema-soft italic leading-relaxed my-4 pl-4 pr-3 py-3 border-l-2 border-cinema-glow/50 bg-cinema-glow/5 rounded-r`}>
              {children}
            </blockquote>
          ),
          // Links — glow color, underline on hover
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-cinema-glow hover:text-cinema-amber underline underline-offset-2 transition-colors"
            >
              {children}
            </a>
          ),
          // Horizontal rule — subtle divider
          hr: () => (
            <hr className="my-5 border-t border-white/10" />
          ),
          // Tables — clean with header row, scrollable on mobile
          table: ({ children }) => (
            <div className="my-4 overflow-x-auto">
              <table className="w-full text-xs sm:text-sm border-collapse">
                {children}
              </table>
            </div>
          ),
          thead: ({ children }) => (
            <thead className="bg-white/5">
              {children}
            </thead>
          ),
          th: ({ children }) => (
            <th className="font-sans font-semibold text-white text-left px-3 py-2 border border-white/10">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="font-sans text-cinema-soft px-3 py-2 border border-white/8">
              {children}
            </td>
          ),
          // Strikethrough
          del: ({ children }) => (
            <del className="line-through text-cinema-muted">
              {children}
            </del>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
